import React, { useEffect, useMemo, useState } from "react";
import { ModalProps } from "react-bootstrap";
import { Link45deg, Plus, X } from "react-bootstrap-icons";
import Select from "react-select";
import * as Yup from "yup";
import { BaseButton, Error, TextInput, selectTheme } from "../../../../libs";
import { ReferralProgram } from "../../types";
import * as Styled from "./InviteReferrerModal.styled";

interface Props extends ModalProps {
  program: ReferralProgram;
  onInvite: (data: { email: string; invitationUrl?: string }) => Promise<void>;
  onAddDestination: (url: string) => Promise<void>;
}

const emailSchema = Yup.string()
  .email("* Enter a valid email address")
  .required("* Email is required");
const validUrl = (value: string) => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

export const InviteReferrerModal: React.FC<Props> = ({
  program,
  onInvite,
  onAddDestination,
  ...props
}) => {
  const destinations = useMemo(() => program.destinationUrls || [], [program.destinationUrls]);
  const destinationOptions = destinations.map((item) => ({ value: item, label: item }));
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [invitationUrl, setInvitationUrl] = useState("");
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    setInvitationUrl((current) =>
      destinations.includes(current) ? current : destinations[0] || ""
    );
  }, [destinations]);

  const close = () => {
    setEmail("");
    setEmailError("");
    setInvitationUrl(destinations[0] || "");
    setShowUrlForm(false);
    setUrl("");
    setUrlError("");
    props.onHide?.();
  };

  const saveUrl = async () => {
    const value = url.trim();
    if (!validUrl(value)) {
      setUrlError("* Enter a URL beginning with http:// or https://");
      return;
    }
    setSavingUrl(true);
    setUrlError("");
    try {
      await onAddDestination(value);
      setInvitationUrl(value);
      setUrl("");
      setShowUrlForm(false);
    } catch {
      // The container displays the request error.
    } finally {
      setSavingUrl(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await emailSchema.validate(email.trim());
      setEmailError("");
    } catch (error) {
      setEmailError(
        error instanceof Yup.ValidationError ? error.message : "* Enter a valid email address"
      );
      return;
    }
    if (!invitationUrl) {
      setUrlError("* Add a destination URL before sending the invitation");
      setShowUrlForm(true);
      return;
    }
    setSubmitting(true);
    try {
      await onInvite({ email: email.trim(), invitationUrl });
      close();
    } catch {
      // The container displays the request error.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Styled.InviteModal {...props} centered keyboard={false}>
      <Styled.ModalWrapper>
        <Styled.Header>
          <div>
            <Styled.ModalTitle>Invite a Referrer</Styled.ModalTitle>
            <Styled.Subtitle>
              Add their email and choose where the referral link should send them.
            </Styled.Subtitle>
          </div>
          <Styled.CloseButton type="button" onClick={close} aria-label="Close">
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>

        <form onSubmit={submit}>
          <Styled.Fields>
            <Styled.Field>
              {emailError && <Error message={emailError} />}
              <TextInput
                fieldName="referrerEmail"
                fieldLabel="Email Address *"
                placeholder="name@example.com"
                fieldValue={email}
                onChangeHandler={(event) => {
                  setEmail((event.target as HTMLInputElement).value);
                  setEmailError("");
                }}
                hasError={!!emailError}
              />
            </Styled.Field>

            <Styled.Field>
              <Styled.FieldLabel>Invitation Destination *</Styled.FieldLabel>
              {destinations.length > 0 ? (
                <Select
                  styles={selectTheme.singleSelect()}
                  options={destinationOptions}
                  value={
                    destinationOptions.find((option) => option.value === invitationUrl) || null
                  }
                  onChange={(option) => setInvitationUrl(option?.value || "")}
                />
              ) : (
                <Styled.MissingDestination>
                  <span>No destination URL added</span>
                  <Styled.InlineAction type="button" onClick={() => setShowUrlForm(true)}>
                    <Link45deg /> Add URL
                  </Styled.InlineAction>
                </Styled.MissingDestination>
              )}
            </Styled.Field>

            {destinations.length > 0 && !showUrlForm && (
              <Styled.AddAnother type="button" onClick={() => setShowUrlForm(true)}>
                <Plus /> Add another destination URL
              </Styled.AddAnother>
            )}

            {showUrlForm && (
              <Styled.UrlSection>
                <Styled.Field>
                  {urlError && <Error message={urlError} />}
                  <TextInput
                    fieldName="newDestinationUrl"
                    fieldLabel="New Destination URL *"
                    placeholder="https://example.com/watch"
                    fieldValue={url}
                    onChangeHandler={(event) => {
                      setUrl((event.target as HTMLInputElement).value);
                      setUrlError("");
                    }}
                    hasError={!!urlError}
                  />
                </Styled.Field>
                <Styled.UrlActions>
                  <Styled.TextButton
                    type="button"
                    onClick={() => {
                      setShowUrlForm(false);
                      setUrl("");
                      setUrlError("");
                    }}
                  >
                    Cancel
                  </Styled.TextButton>
                  <Styled.SmallButton>
                    <BaseButton type="button" onClick={saveUrl} disabled={savingUrl}>
                      {savingUrl ? "Saving..." : "Save URL"}
                    </BaseButton>
                  </Styled.SmallButton>
                </Styled.UrlActions>
              </Styled.UrlSection>
            )}
          </Styled.Fields>

          <Styled.Footer>
            <Styled.ButtonSlot>
              <BaseButton type="button" variant="secondary" onClick={close}>
                Cancel
              </BaseButton>
            </Styled.ButtonSlot>
            <Styled.ButtonSlot>
              <BaseButton type="submit" disabled={submitting || !invitationUrl}>
                {submitting ? "Sending..." : "Add & Send Email"}
              </BaseButton>
            </Styled.ButtonSlot>
          </Styled.Footer>
        </form>
      </Styled.ModalWrapper>
    </Styled.InviteModal>
  );
};
